'use client';

import { useMemo, useState } from 'react';

import { MostSearchedResult, MostSearchedResultType } from '@/types/most-search-type';
import { ProceduresCard } from
  '@/app/(main)/(pages)/_components/most-searched-section/default/procedures-card.component';
import { ProceduresFilter } from
  '@/app/(main)/(pages)/_components/most-searched-section/default/procedures-filter.component';
import { cardDisplayMostSearched } from '@/app/(main)/(pages)/(home)/_consts/card-display-most-searched.consts';
import { useIsGreaterThan } from '@/hooks/use-is-greather-than.hook';
import { handleOpenSearchModal } from '@/store/search/handle-open-search-modal';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';
import { MostSearchedType, defaultTerms } from '@/app/(main)/(pages)/_types/most-searched.type';

interface MostSearchedSectionInteractiveProps {
  procedures: MostSearchedResultType[];
  defaultFilter: MostSearchedType;
}

export function MostSearchedSectionInteractive({
  procedures,
  defaultFilter,
}: MostSearchedSectionInteractiveProps) {
  const analytics = getAnalyticsPlugin();
  const isSingleCard = !useIsGreaterThan('xl');

  const [selectedFilter, setFilter] = useState<MostSearchedType>(defaultFilter);

  const [cardOne, cardTwo] = useMemo(() => {
    const list = procedures?.find((item) => item.type === selectedFilter);
    const cards = cardDisplayMostSearched({ isSingleCard, selectedProcedureList: list?.procedures ?? [] });

    return [cards[0], cards[1]];
  }, [selectedFilter, procedures, isSingleCard]);

  const goToSearchProcedureSchedules = (mostSearchedResultOption: MostSearchedResult): void => {
    analytics.track(AnalyticsEnum.CLICK_MOST_SEARCHED_PROCEDURE, {
      procedimento: mostSearchedResultOption?.name?.toLocaleLowerCase(),
      procedimento_label: mostSearchedResultOption?.displayName?.toLocaleLowerCase(),
      categoria: selectedFilter.toLocaleLowerCase(),
      categoria_label: defaultTerms[selectedFilter],
    });
    handleOpenSearchModal({ searchTerm: mostSearchedResultOption.displayName });
  };

  const onSeeMore = (): void => {
    analytics.track(AnalyticsEnum.CLICK_MOST_SEARCHED_SEE_MORE);
    handleOpenSearchModal({ searchTerm: defaultTerms[selectedFilter as MostSearchedType] });
  };

  const handleChangeFilter = (filter: MostSearchedType) => {
    setFilter(filter);
    analytics.track(AnalyticsEnum.CLICK_MOST_SEARCHED_CATEGORY, {
      categoria: filter.toLocaleLowerCase(),
      categoria_label: defaultTerms[filter],
    });
  };

  return (
    <>
      <ProceduresFilter
        onFilterSelect={handleChangeFilter}
        selectedFilter={selectedFilter}
        procedures={procedures}
      />

      <div className="flex gap-6 w-full">
        <div className="w-full xl:w-[31.25rem] 3xl:w-[40.625rem]">
          <ProceduresCard
            procedures={cardOne}
            onSeeMore={isSingleCard ? onSeeMore : undefined}
            goToSearchProcedureSchedules={goToSearchProcedureSchedules}
          />
        </div>
        {!isSingleCard && (
          <div className="w-full xl:w-[31.25rem] 3xl:w-[40.625rem]">
            <ProceduresCard
              procedures={cardTwo}
              onSeeMore={onSeeMore}
              goToSearchProcedureSchedules={goToSearchProcedureSchedules}
            />
          </div>
        )}
      </div>
    </>
  );
}
